import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { formatFinance } from "../../../../utils/formatters";

// Componente de tooltip personalizado para mostrar valores de forma mais clara
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Ordenar os valores no tooltip do maior para o menor para facilitar a leitura
    const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

    return (
      <div
        style={{
          backgroundColor: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "5px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <p style={{ margin: "0", fontWeight: "bold" }}>{label}</p>
        {sortedPayload.map((entry, index) => (
          <p key={`item-${index}`} style={{ margin: "0", color: entry.color }}>
            {entry.name}: {formatFinance(entry.value)}
          </p>
        ))}
        <p
          style={{
            margin: "0",
            borderTop: "1px solid #eee",
            paddingTop: "5px",
          }}
        >
          Total:{" "}
          {formatFinance(
            sortedPayload.reduce((sum, entry) => sum + entry.value, 0)
          )}
        </p>
      </div>
    );
  }
  return null;
};

const HourlyDistributionChart = ({ data, loading }) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Processar os dados para determinar qual série tem maior valor médio
  const getSeriesAverages = () => {
    if (!data || data.length === 0)
      return { indoor: 0, takeout: 0, delivery: 0 };

    const sums = data.reduce(
      (acc, item) => {
        return {
          indoor: acc.indoor + (item.indoor || 0),
          takeout: acc.takeout + (item.takeout || 0),
          delivery: acc.delivery + (item.delivery || 0),
        };
      },
      { indoor: 0, takeout: 0, delivery: 0 }
    );

    return {
      indoor: sums.indoor / data.length,
      takeout: sums.takeout / data.length,
      delivery: sums.delivery / data.length,
    };
  };

  // Obter as médias e determinar a ordem
  const averages = getSeriesAverages();

  // Criar um array ordenado de menor para maior valor médio
  const seriesOrder = Object.entries(averages)
    .sort((a, b) => a[1] - b[1])
    .map((item) => item[0]);

  // Mapear os nomes das séries para seus rótulos em português
  const seriesLabels = {
    indoor: "Salão",
    takeout: "Retirada",
    delivery: "Delivery",
  };

  // Mapear as cores para cada série
  const seriesColors = {
    indoor: "#8884d8", // roxo
    takeout: "#ffc658", // amarelo
    delivery: "#82ca9d", // verde
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <ScheduleIcon sx={{ mr: 1, color: "secondary.main" }} />
        <Typography variant="h6">Vendas por Hora</Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Vendas por hora do dia
      </Typography>

      <Box sx={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />

            {/* Renderizar áreas com base na ordem determinada dinamicamente */}
            {seriesOrder.map((series) => (
              <Area
                key={series}
                type="monotone"
                dataKey={series}
                stackId="1"
                stroke={seriesColors[series]}
                fill={seriesColors[series]}
                name={seriesLabels[series]}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </>
  );
};

export default HourlyDistributionChart;
